/**
 * UsersListTab — Aba 1 "Lista" da tela A1 Gestão Usuários (Brief 5 A1, W3.1).
 *
 * Pattern Opção A: capability-driven, silent fallback.
 * Pattern D-B: cada ação destrutiva (deactivate / bulk) tem próprio gate.
 *
 * Funcionalidades:
 *   - Tabela paginada cursor com filtros (status + role).
 *   - Bulk actions: desativar selecionados (gate users-deactivate).
 *   - Click na row → abre Detalhe (callback onSelectUser).
 *   - EmptyState se capability users-read ausente.
 */

import { useState } from 'react';

import { useCapability } from '../../../hooks/use-capability';
import { useDeactivateUser, useUsers } from '../../../hooks/use-users';

import { USER_ROLE_LABELS } from './types';
import { UserAvatar } from './UserAvatar';
import { UserStatusBadge } from './UserStatusBadge';

import type { ListUsersParams, UserWireItem } from './types';

const ROLE_OPTIONS = [
  { value: '', label: 'Todos os papéis' },
  { value: 'atendente', label: 'Atendente' },
  { value: 'supervisor', label: 'Supervisor' },
  { value: 'gestor', label: 'Gestor' },
  { value: 'admin_tenant', label: 'Admin' },
  { value: 'dpo_tenant', label: 'DPO' },
];

const STATUS_OPTIONS = [
  { value: '', label: 'Todos os status' },
  { value: 'active', label: 'Ativo' },
  { value: 'archived', label: 'Desativado' },
  { value: 'suspended', label: 'Suspenso' },
  { value: 'on_leave', label: 'Em licença' },
];

interface Props {
  onSelectUser: (user: UserWireItem) => void;
}

export function UsersListTab({ onSelectUser }: Props): JSX.Element {
  const canRead = useCapability('users-read');
  const canDeactivate = useCapability('users-deactivate');

  const [roleFilter, setRoleFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('active');
  const [cursor, setCursor] = useState<string | undefined>(undefined);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [deactivateReason, setDeactivateReason] = useState('');
  const [showBulkModal, setShowBulkModal] = useState(false);

  const usersParams: ListUsersParams = { limit: 20 };
  if (statusFilter) usersParams.status = statusFilter;
  if (roleFilter) usersParams.role = roleFilter;
  if (cursor !== undefined) usersParams.cursor = cursor;
  const { data, isLoading, isError } = useUsers(usersParams);

  const deactivateMut = useDeactivateUser();

  if (!canRead) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center">
        <span className="text-4xl">🔒</span>
        <p className="mt-3 text-sm font-medium text-text-secondary">
          Sem permissão para listar usuários
        </p>
        <p className="mt-1 text-xs text-text-secondary">
          A lista de usuários é restrita a gestores e administradores do tenant.
        </p>
      </div>
    );
  }

  const users: UserWireItem[] = data?.data ?? [];
  const nextCursor = data?.nextCursor ?? null;
  const total = data?.total ?? 0;

  function toggleSelect(id: string): void {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function toggleAll(): void {
    if (selected.size === users.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(users.map((u) => u.id)));
    }
  }

  async function handleBulkDeactivate(): Promise<void> {
    if (deactivateReason.trim().length < 5) return;
    for (const id of selected) {
      await deactivateMut.mutateAsync({ id, body: { reason: deactivateReason.trim() } });
    }
    setSelected(new Set());
    setShowBulkModal(false);
    setDeactivateReason('');
  }

  function formatLastLogin(val: string | null): string {
    if (!val) return '—';
    const d = new Date(val);
    return d.toLocaleDateString('pt-BR', { day: '2-digit', month: 'short', year: 'numeric' });
  }

  return (
    <div className="flex flex-col gap-4">
      {/* Filtros */}
      <div className="flex flex-wrap items-center gap-3">
        <select
          value={statusFilter}
          onChange={(e) => {
            setStatusFilter(e.target.value);
            setCursor(undefined);
          }}
          className="rounded border border-border-subtle bg-surface px-3 py-1.5 text-sm"
          aria-label="Filtrar por status"
        >
          {STATUS_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
        <select
          value={roleFilter}
          onChange={(e) => {
            setRoleFilter(e.target.value);
            setCursor(undefined);
          }}
          className="rounded border border-border-subtle bg-surface px-3 py-1.5 text-sm"
          aria-label="Filtrar por papel"
        >
          {ROLE_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
        <span className="ml-auto text-sm text-text-secondary">
          {total > 0 ? `${total.toString()} usuário${total !== 1 ? 's' : ''}` : ''}
        </span>

        {/* D-B: Bulk deactivate — gate próprio users-deactivate */}
        {canDeactivate && selected.size > 0 && (
          <button
            type="button"
            onClick={() => setShowBulkModal(true)}
            className="rounded bg-red-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-red-700"
          >
            Desativar {selected.size} selecionado{selected.size !== 1 ? 's' : ''}
          </button>
        )}
      </div>

      {/* Tabela */}
      {isLoading && (
        <div className="flex items-center justify-center py-12">
          <span className="text-sm text-text-secondary">Carregando...</span>
        </div>
      )}

      {isError && (
        <div className="rounded border border-red-200 bg-red-50 p-4 text-sm text-red-700">
          Erro ao carregar usuários.
        </div>
      )}

      {!isLoading && !isError && (
        <div className="overflow-x-auto rounded-lg border border-border-subtle">
          <table className="min-w-full divide-y divide-border-subtle text-sm">
            <thead className="bg-surface-raised">
              <tr>
                {canDeactivate && (
                  <th className="w-10 px-4 py-3">
                    <input
                      type="checkbox"
                      checked={users.length > 0 && selected.size === users.length}
                      onChange={toggleAll}
                      aria-label="Selecionar todos"
                    />
                  </th>
                )}
                <th className="px-4 py-3 text-left font-medium text-text-secondary">Usuário</th>
                <th className="px-4 py-3 text-left font-medium text-text-secondary">Papel</th>
                <th className="px-4 py-3 text-left font-medium text-text-secondary">Status</th>
                <th className="px-4 py-3 text-left font-medium text-text-secondary">
                  Último login
                </th>
                <th className="px-4 py-3 text-left font-medium text-text-secondary">
                  Reporta para
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border-subtle bg-surface">
              {users.length === 0 && (
                <tr>
                  <td
                    colSpan={canDeactivate ? 6 : 5}
                    className="px-4 py-8 text-center text-text-secondary"
                  >
                    Nenhum usuário encontrado.
                  </td>
                </tr>
              )}
              {users.map((user) => (
                <tr
                  key={user.id}
                  className="cursor-pointer transition-colors hover:bg-surface-raised"
                  onClick={() => onSelectUser(user)}
                >
                  {canDeactivate && (
                    <td
                      className="w-10 px-4 py-3"
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleSelect(user.id);
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={selected.has(user.id)}
                        onChange={() => toggleSelect(user.id)}
                        onClick={(e) => e.stopPropagation()}
                        aria-label={`Selecionar ${user.name}`}
                      />
                    </td>
                  )}
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      <UserAvatar id={user.id} name={user.name} size="sm" />
                      <div>
                        <p className="font-medium text-text-primary">{user.name}</p>
                        <p className="text-xs text-text-secondary">{user.email}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-text-secondary">
                    {USER_ROLE_LABELS[user.primaryRole] ?? user.primaryRole}
                  </td>
                  <td className="px-4 py-3">
                    <UserStatusBadge status={user.status} />
                  </td>
                  <td className="px-4 py-3 text-text-secondary">
                    {formatLastLogin(user.lastLoginAt)}
                  </td>
                  <td className="px-4 py-3 text-xs text-text-secondary">
                    {user.reportsToUserId ? (
                      <span className="font-mono">{user.reportsToUserId.slice(0, 8)}…</span>
                    ) : (
                      '—'
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Paginação */}
      {!isLoading && !isError && (
        <div className="flex items-center justify-between">
          <button
            type="button"
            onClick={() => setCursor(undefined)}
            disabled={cursor === undefined}
            className="rounded border border-border-subtle px-3 py-1.5 text-sm disabled:opacity-40"
          >
            Início
          </button>
          <button
            type="button"
            onClick={() => {
              if (nextCursor) setCursor(nextCursor);
            }}
            disabled={!nextCursor}
            className="rounded border border-border-subtle px-3 py-1.5 text-sm disabled:opacity-40"
          >
            Próxima página
          </button>
        </div>
      )}

      {/* Modal bulk deactivate */}
      {showBulkModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="w-full max-w-md rounded-lg bg-surface p-6 shadow-xl">
            <h3 className="text-base font-semibold text-text-primary">
              Desativar {selected.size} usuário{selected.size !== 1 ? 's' : ''}
            </h3>
            <p className="mt-2 text-sm text-text-secondary">
              Esta ação é reversível: o histórico é preservado para auditoria.
            </p>
            <label className="mt-4 block text-sm font-medium text-text-primary">
              Motivo (obrigatório, mínimo 5 caracteres)
            </label>
            <textarea
              value={deactivateReason}
              onChange={(e) => setDeactivateReason(e.target.value)}
              rows={3}
              className="mt-1 w-full rounded border border-border-subtle bg-surface px-3 py-2 text-sm"
              placeholder="Ex: Desligamento da empresa"
            />
            <div className="mt-4 flex gap-3 justify-end">
              <button
                type="button"
                onClick={() => {
                  setShowBulkModal(false);
                  setDeactivateReason('');
                }}
                className="rounded border border-border-subtle px-4 py-2 text-sm"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={() => void handleBulkDeactivate()}
                disabled={deactivateReason.trim().length < 5 || deactivateMut.isPending}
                className="rounded bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-40"
              >
                {deactivateMut.isPending ? 'Processando...' : 'Confirmar'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
